import React from 'react';

import { useCreateUser } from '@vlabs/api/artemy/client';
import type {
  UserId,
  UserToBeCreated,
} from '@vlabs/api/artemy/model';
import { PlusIcon } from '@vlabs/icons';
import { withPermissions } from '@vlabs/permissions';
import { Control, useModal } from '@vlabs/uikit';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button/Button';
import { toast } from 'react-toastify';

import { UserForm } from '@vlabs/monokit/entities/users';
import type { UserFormValues } from '@vlabs/monokit/entities/users';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useCreateUserTranslation } from './i18n';

const displayName = 'CreateUserButton';
const testid = buildTestid(displayName);

interface CreateUserButtonProps extends Omit<ButtonProps, 'onClick'> {
  onCreateUser?: (userId: UserId) => void;
}

function CreateUserButtonBase({
  onCreateUser = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: CreateUserButtonProps): React.JSX.Element {
  const { t } = useCreateUserTranslation();
  const createUserModal = useModal({ size: 'small' });
  const createUser = useCreateUser({
    mutation: {
      onSuccess: ({ user_id: userId }) => {
        createUserModal.close();
        toast.success(t('confirmation_success'));
        onCreateUser?.(userId);
      },
      onError: () => {
        toast.error(t('confirmation_error'));
      },
    },
  });

  const handleSubmit = (values: UserFormValues) => {
    createUser.mutate({
      data: values as UserToBeCreated,
    });
  };

  return (
    <React.Fragment>
      {createUserModal.wrap(
        <UserForm
          context="create"
          onCancel={createUserModal.close}
          onSubmit={handleSubmit}
        />,
      )}

      <Control.Button
        icon={<PlusIcon />}
        onClick={createUserModal.open}
        {...testid(dataTestId)}
        {...props}
      >
        {props?.children ?? t('add_button')}
      </Control.Button>
    </React.Fragment>
  );
}

CreateUserButtonBase.displayName = displayName;
const CreateUserButton = withPermissions('users', 'create', { fallback: null })(CreateUserButtonBase);
export { CreateUserButton };
